import type { PermissionCode } from '@asyagrup/group-hub-contracts';
import type { LucideIcon } from 'lucide-react';
import { NAV, NAV_SECTIONS, type NavItem } from '@/app/nav';

/**
 * "Ekranlar" — the first client-side tier, rendered at 0 ms.
 *
 * Nothing here touches the network: the permitted nav is already in memory, so
 * the palette has something correct on screen before the debounce has even
 * started. It reuses `AppShell`'s own filter verbatim — `requires.every(can)`
 * — rather than a second copy of the rule, so a screen that is hidden from the
 * menu is hidden from search by the same expression, not by a parallel one
 * that can drift.
 */

export interface ScreenHit {
  key: string;
  label: string;
  /** The section heading, so "Rezervasyonlar" says which module it belongs to. */
  section: string;
  to: string;
  icon: LucideIcon;
}

const SECTION_LABELS = new Map(NAV_SECTIONS.map((section) => [section.key, section.label]));

/**
 * `toLocaleLowerCase('tr')` and never the bare form.
 *
 * In this locale 'I' lowercases to 'ı' and 'İ' to 'i'. With the invariant
 * casing, "İzinlerim" does not match a typed "izin" — the exact word the
 * screen exists for. This is the single most likely way a Turkish search box
 * silently fails to find the thing that is right there.
 */
export function fold(value: string): string {
  return value.trim().toLocaleLowerCase('tr');
}

/**
 * Yazılan kelime ekranın bir YARISINI adlandırıyorsa o yarının adresi.
 *
 * «Destek ve Yenilikler» tek satır; «yenilik» yazıp Enter'a basan sürüm
 * notlarına gitmek istiyor — satırın `to`su ise Destek yarısını açıyor.
 * Yarılar `NavItem.sections`'ta; kişinin giremediği yarı atlanıyor, hiçbir
 * yarı adlanmıyorsa satırın kendi adresi.
 */
function yariAdresi(
  item: NavItem,
  needle: string,
  can: (permission: PermissionCode) => boolean,
): string {
  const yari = item.sections?.find(
    (s) => s.requires.every((p) => can(p)) && fold(s.label).includes(needle),
  );
  return yari?.href ?? item.to;
}

export function matchScreens(
  term: string,
  can: (permission: PermissionCode) => boolean,
  limit = 6,
): ScreenHit[] {
  const needle = fold(term);
  if (needle.length < 2) return [];

  return (
    NAV.filter((item) => item.ready && item.requires.every((p) => can(p)))
      .filter((item) => fold(item.label).includes(needle))
      // A prefix match is what the person meant; a mid-word match is a bonus.
      .sort((a, b) => {
        const aPrefix = fold(a.label).startsWith(needle) ? 0 : 1;
        const bPrefix = fold(b.label).startsWith(needle) ? 0 : 1;
        return aPrefix - bPrefix || a.label.localeCompare(b.label, 'tr');
      })
      .slice(0, limit)
      .map((item) => ({
        key: item.key,
        label: item.label,
        section: SECTION_LABELS.get(item.section) ?? '',
        to: yariAdresi(item, needle, can),
        icon: item.icon,
      }))
  );
}

const BY_PATH = new Map(NAV.map((item) => [item.to, item]));

function pathOf(href: string): string {
  return href.split('?')[0] ?? href;
}

/**
 * The nav key for an in-app href, so a highlighted row can warm its chunk.
 *
 * Query strings are stripped: a search hit's href is `/destek?kayit=<id>`, and
 * the chunk behind it is the one `/destek` loads.
 */
export function navKeyForHref(href: string): string | undefined {
  return BY_PATH.get(pathOf(href))?.key;
}

/** The screen's own menu label, for the "there is more, go there" row. */
export function navLabelForHref(href: string): string | undefined {
  return BY_PATH.get(pathOf(href))?.label;
}
